"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Award,
  MapPin,
  Clock,
  Music,
  Youtube,
  Link as LinkIcon,
  ExternalLink,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Image from "next/image";
import { getInstructorById } from "@/lib/api/lessons";

interface ProProfileCardProps {
  proId: string;
  proName?: string;
  className?: string;
}

interface ProProfileData {
  id: string;
  name: string;
  photoUrl?: string;
  bio?: string;
  location?: string;
  experience?: string;
  specialty?: string[];
  certifications?: string[];
  portfolioImages?: string[];
  videoUrls?: string[];
  youtubeUrl?: string;
  portfolioUrl?: string;
  tags?: string[];
}

export function ProProfileCard({ proId, proName, className = "" }: ProProfileCardProps) {
  const [profile, setProfile] = useState<ProProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showAllImages, setShowAllImages] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!proId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // 실제 instructors 컬렉션에서 직접 데이터 가져오기
        const { getFirebaseDb } = await import("@/lib/api/firebase-ultra-safe");
        const { getDoc, doc } = await import("firebase/firestore");
        
        const db = getFirebaseDb();
        if (!db) {
          setLoading(false);
          return;
        }
        
        const instructorDoc = await getDoc(doc(db, "instructors", proId));
        const data = instructorDoc.exists() ? instructorDoc.data() : null;
        
        if (data) {
          setProfile({
            id: proId,
            name: data.name || proName || "전문가",
            photoUrl: data.photoUrl || data.photo || undefined,
            bio: data.bio || undefined,
            location: data.location || undefined,
            experience: data.experience || undefined,
            specialty: Array.isArray(data.specialty) 
              ? data.specialty 
              : data.specialty 
                ? [data.specialty] 
                : undefined,
            certifications: data.certifications || undefined,
            portfolioImages: data.portfolioImages || undefined,
            videoUrls: data.videoUrls || undefined,
            youtubeUrl: data.youtubeUrl || undefined,
            portfolioUrl: data.portfolioUrl || undefined,
            tags: data.tags || undefined,
          });
        } else {
          // Fallback: getInstructorById 사용
          const instructor = await getInstructorById(proId);
          if (instructor) {
            setProfile({
              id: instructor.id,
              name: instructor.name || proName || "전문가",
              photoUrl: instructor.photo || undefined,
              bio: instructor.bio || undefined,
              location: instructor.location || undefined,
              experience: instructor.experience || undefined,
              specialty: Array.isArray(instructor.specialty) 
                ? instructor.specialty 
                : instructor.specialty 
                  ? [instructor.specialty] 
                  : undefined,
              certifications: instructor.certifications || undefined,
              tags: instructor.tags || undefined,
            });
          }
        }
      } catch (error) {
        console.error("프로 프로필 로드 실패:", error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [proId, proName]);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-24"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const hasAdditionalInfo = profile.bio || profile.experience || profile.certifications?.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}
    >
      {/* 프로필 헤더 */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
            {profile.photoUrl ? (
              <Image
                src={profile.photoUrl}
                alt={profile.name}
                fill
                className="object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-6 h-6 text-gray-400" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate">
              {profile.name}
            </h3>
            {profile.location && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{profile.location}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 프로필 내용 */}
      <div className="p-4 space-y-3">
        {/* 전문 분야 */}
        {profile.specialty && profile.specialty.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
              <Music className="w-3.5 h-3.5" />
              <span>전문 분야</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {profile.specialty.slice(0, expanded ? undefined : 5).map((item, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md"
                >
                  {item}
                </span>
              ))}
              {profile.specialty.length > 5 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  {expanded ? (
                    <span className="flex items-center gap-1">
                      접기 <ChevronUp className="w-3 h-3" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      +{profile.specialty.length - 5}개 더보기
                      <ChevronDown className="w-3 h-3" />
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* 간단한 소개 */}
        {profile.bio && (
          <div>
            <p className="text-xs text-gray-600 line-clamp-3">{profile.bio}</p>
          </div>
        )}

        {/* 경력 */}
        {profile.experience && (
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
              <Clock className="w-3.5 h-3.5" />
              <span>경력</span>
            </div>
            <p className="text-xs text-gray-600">{profile.experience}</p>
          </div>
        )}

        {/* 자격증 */}
        {profile.certifications && profile.certifications.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
              <Award className="w-3.5 h-3.5" />
              <span>자격증</span>
            </div>
            <div className="space-y-1">
              {profile.certifications.map((cert, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-xs text-gray-600"
                >
                  <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
                  <span>{cert}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 태그 */}
        {profile.tags && profile.tags.length > 0 && (
          <div>
            <div className="flex flex-wrap gap-1.5">
              {profile.tags.slice(0, 5).map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 외부 링크 */}
        {(profile.youtubeUrl || profile.portfolioUrl) && (
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            {profile.youtubeUrl && (
              <a
                href={profile.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
              >
                <Youtube className="w-4 h-4" />
                <span>유튜브</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {profile.portfolioUrl && (
              <a
                href={profile.portfolioUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
              >
                <LinkIcon className="w-4 h-4" />
                <span>포트폴리오</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

